import React from 'react';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import SlowMotionVideoRoundedIcon from '@mui/icons-material/SlowMotionVideoRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded';
import ZoomOutRoundedIcon from '@mui/icons-material/ZoomOutRounded';
import Tooltip from '@src/components/Tooltip';
import { MAX_ZOOM, MIN_ZOOM } from '@src/constants/timeline';
import useLiveTime from '@src/hooks/useLiveTime';
import useTimeLabel from '@src/hooks/useTimeLabel';
import { clamp, formatTimelineTime } from '@src/services/timeline';
import {
  StyledCenter,
  StyledConvertingText,
  StyledFitButton,
  StyledGenerateButton,
  StyledGenerateSlot,
  StyledIconButton,
  StyledPlayButton,
  StyledStatusSlot,
  StyledSide,
  StyledTime,
  StyledToolbar,
  StyledZoomFill,
  StyledZoomInput,
  StyledZoomSlider,
} from './index.style';

/**
 * Timeline control bar: collapse · (create-audio, play, time) · (zoom, fit).
 * Presentational — every action is a prop; no scale/redux knowledge here.
 * While synthesising, the convert button is replaced by a plain "creating
 * audio (x%)" status line (per design); play is a preview control, enabled
 * whenever there is content and only blocked mid-convert (`isPlayDisabled`).
 */
const TimelineToolbar = ({
  collapsed = false,
  onToggleCollapse,
  // The collapsed panel draws a progress slider flush above this row, which
  // becomes the dock's top edge — a border here would double it.
  hideTopBorder = false,
  createAudioLabel,
  createAudioTooltip,
  convertingLabel,
  onCreateAudio,
  isCreateAudioDisabled = false,
  // Hidden while a filter narrows the editor: the button converts the WHOLE
  // project. Hidden in place (see StyledGenerateSlot) so the toolbar keeps its
  // layout instead of jumping.
  isCreateAudioHidden = false,
  isConverting = false,
  isPlaying = false,
  onPlayPause,
  isPlayDisabled = false,
  currentSec = 0,
  getSec,
  durationSec = 0,
  zoomLevel = MIN_ZOOM,
  onZoomIn,
  onZoomOut,
  onZoomChange,
  onFit,
}) => {
  // The clock ticks straight into the DOM while playing, so playback never
  // re-renders this toolbar (or anything above it) just to move a label.
  const [clockRef, drawClock] = useTimeLabel();
  useLiveTime(currentSec, isPlaying, drawClock, getSec);

  const handleCreateAudio = () => {
    if (isCreateAudioDisabled || isConverting) return;
    onCreateAudio?.();
  };

  const handlePlayPause = () => {
    if (isPlayDisabled) return;
    onPlayPause?.();
  };

  // Percent of the slider filled (navy) up to the current zoom level.
  const zoomFillPercent = clamp(
    ((zoomLevel - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)) * 100,
    0,
    100,
  );

  return (
    <StyledToolbar hideTopBorder={hideTopBorder}>
      <StyledSide>
        <StyledIconButton
          type="button"
          onClick={onToggleCollapse}
          aria-label="toggle timeline"
        >
          <KeyboardArrowDownRoundedIcon
            style={{ transform: collapsed ? 'rotate(180deg)' : 'none' }}
          />
        </StyledIconButton>
      </StyledSide>

      <StyledCenter>
        <StyledStatusSlot>
          {isConverting ? (
            <StyledConvertingText>{convertingLabel}</StyledConvertingText>
          ) : (
            // A disabled button fires no pointer events, so the tooltip is
            // anchored to a wrapper — same trick the block toolbars use.
            <Tooltip arrow title={createAudioTooltip || ''} placement="top">
              <StyledGenerateSlot isHidden={isCreateAudioHidden}>
                <StyledGenerateButton
                  type="button"
                  onClick={handleCreateAudio}
                  disabled={isCreateAudioDisabled}
                >
                  <SlowMotionVideoRoundedIcon />
                  {createAudioLabel}
                </StyledGenerateButton>
              </StyledGenerateSlot>
            </Tooltip>
          )}
        </StyledStatusSlot>
        <StyledPlayButton
          type="button"
          onClick={handlePlayPause}
          disabled={isPlayDisabled}
          aria-label={isPlaying ? 'pause' : 'play'}
        >
          {isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
        </StyledPlayButton>
        <StyledTime>
          <span ref={clockRef}>{formatTimelineTime(currentSec)}</span>
          {` / ${formatTimelineTime(durationSec)}`}
        </StyledTime>
      </StyledCenter>

      <StyledSide end>
        <StyledIconButton
          type="button"
          onClick={onZoomOut}
          aria-label="zoom out"
        >
          <ZoomOutRoundedIcon />
        </StyledIconButton>
        <StyledZoomSlider>
          <StyledZoomFill style={{ width: `${zoomFillPercent}%` }} />
          <StyledZoomInput
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.1}
            value={zoomLevel}
            onChange={(event) => onZoomChange(Number(event.target.value))}
            aria-label="zoom level"
          />
        </StyledZoomSlider>
        <StyledIconButton type="button" onClick={onZoomIn} aria-label="zoom in">
          <ZoomInRoundedIcon />
        </StyledIconButton>
        <StyledFitButton type="button" onClick={onFit}>
          Fit
        </StyledFitButton>
      </StyledSide>
    </StyledToolbar>
  );
};

export default TimelineToolbar;
